import { Component, type ReactNode } from 'react';
import { Platform, View } from 'react-native';
import { colors, t, type Lang } from '@hogar/shared';
import { Button, Txt } from './ui';
import { openHelpEmail } from '../lib/help';
import { reportAppError } from '../lib/monitoring';

/**
 * When a screen crashes, a calm screen with its reference, "Try again," and "Get help" instead of
 * a blank page (company standard, section 6). The crash goes to Sentry under the same reference.
 */
export class ErrorBoundary extends Component<{ lang: Lang; children: ReactNode }, { reference: string | null }> {
  state = { reference: null as string | null };

  static getDerivedStateFromError() {
    return { reference: `c-${Math.random().toString(36).slice(2, 12)}` };
  }

  componentDidCatch(error: Error) {
    if (this.state.reference) reportAppError(error, this.state.reference);
  }

  render() {
    const { reference } = this.state;
    if (!reference) return this.props.children;
    const { lang } = this.props;
    return (
      <View style={{ flex: 1, backgroundColor: colors.background, padding: 24, justifyContent: 'center', gap: 14 }}>
        <Txt weight="semibold" size={20}>
          {t(lang, 'somethingWrong')}
        </Txt>
        <Txt color={colors.textMuted}>{t(lang, 'errorReference', { ref: reference })}</Txt>
        <Button label={t(lang, 'tryAgain')} onPress={() => (Platform.OS === 'web' ? window.location.reload() : this.setState({ reference: null }))} />
        <Button label={t(lang, 'getHelp')} variant="outline" onPress={() => openHelpEmail(lang, reference)} />
      </View>
    );
  }
}
